/**
 * 「钉在左侧」列的本地持久化 —— 按**需求类型**维度记录在 localStorage。
 *
 * 【为何按需求类型分别记录】合作内容的属性列由后端 `schema` 驱动，**不同需求类型的模板不同**
 * （列集合 / 列 code 都不同），故钉住集合必须按需求类型各记一份，不能全局共用。
 * 本目录的 `category_id` 即 l3 需求类型（见 `CooperationFormListProps.category_id`，
 * 导出参数里直接以 `l3_category_id: category_id` 使用）。
 *
 * 【为何单独成文件】纯读写、无 React 依赖，可单测；与同仓既有范式一致
 * （`IntegrityModal/storage.ts`、`ExportFilePollingBtn/cacheUtils.ts`：小 storage 模块 +
 * try/catch 降级，localStorage 不可用时静默降级而不是抛错）。
 *
 * 【存什么】只存**属性列的 code 数组**（如 `['standardCode1', 'DCP0058']`），与内存态
 * `pinnedCodes` 同形。用 code 而不是列下标：列的顺序/数量随模板变化，下标会错位。
 *
 * ⚠️ 【未按用户隔离】localStorage 天然是「一台浏览器一份」。若同一浏览器存在多人共用账号的
 * 情况、需要按人隔离，只需在 `buildKey` 里追加用户标识（调用方需先把 mail/工号传进来）。
 * 该点已列出与需求方确认，当前按「按浏览器」实现。
 */

/** key 前缀：`mpc_cooperate_pinned_columns_{需求类型id}` */
const KEY_PREFIX = 'mpc_cooperate_pinned_columns_';

const buildKey = (categoryId: number | string): string => `${KEY_PREFIX}${categoryId}`;

/** 无需求类型 id 时不落盘也不读取（无法确定归属，宁可不持久化） */
const isValidCategory = (categoryId: unknown): categoryId is number | string =>
  (typeof categoryId === 'number' && !Number.isNaN(categoryId)) ||
  (typeof categoryId === 'string' && categoryId !== '');

/**
 * 读某需求类型已钉住的属性列 code 列表。
 * 无记录 / JSON 损坏 / 结构不对 / localStorage 不可用 → 一律返回 `[]`（调用方按「没有钉住」处理）。
 */
export function readPinnedCodes(categoryId?: number | string | null): string[] {
  if (!isValidCategory(categoryId)) return [];
  try {
    const raw = window.localStorage.getItem(buildKey(categoryId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // 只认字符串项：历史脏数据（数字/对象/undefined）不应把列 code 集合污染成非字符串
    return parsed.filter((code): code is string => typeof code === 'string' && code !== '');
  } catch {
    return [];
  }
}

/**
 * 覆盖写某需求类型已钉住的属性列 code 列表。
 * 语义是**覆盖**（用户每次调整钉住即重新记录该需求类型的完整状态），不是追加。
 */
export function savePinnedCodes(categoryId: number | string | null | undefined, codes: string[]): void {
  if (!isValidCategory(categoryId)) return;
  try {
    window.localStorage.setItem(buildKey(categoryId), JSON.stringify(codes));
  } catch {
    /* noop: localStorage 不可用（隐私模式/容量满）时降级为「仅本次会话生效」，不阻断交互 */
  }
}
